import type { PostHog } from 'posthog-js';
import { eventSchemas, type EventName, type EventProps } from './events';

interface InitParams {
  apiKey: string;
  distinctId: string;
  appVersion: string;
  superProps?: Record<string, unknown>;
  /** When true, call opt_in_capturing() before flushing the pre-init buffer. */
  optedInOnInit?: boolean;
}

interface BufferedCall {
  name: EventName;
  props: Record<string, unknown>;
}

let client: PostHog | null = null;
let buffer: BufferedCall[] = [];
let initPromise: Promise<void> | null = null;

// Stored at init time so deleteMyData() can reference them without
// calling client.config.token / client.get_distinct_id() — those may
// not be available (e.g. in test mocks or before the SDK is fully ready).
let _apiKey: string | null = null;
let _distinctId: string | null = null;
const _apiHost = 'https://us.i.posthog.com';

export async function initPostHog(params: InitParams): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    _apiKey = params.apiKey;
    _distinctId = params.distinctId;

    const { default: posthog } = await import('posthog-js');
    posthog.init(params.apiKey, {
      api_host: _apiHost,
      opt_out_capturing_by_default: true,
      // Explicit opt-in, respecting GPC.
      respect_dnt: false,
      autocapture: false,
      capture_pageview: 'history_change',
      capture_pageleave: false,
      disable_session_recording: true,
      persistence: 'localStorage',
      property_denylist: ['$ip'],
      mask_personal_data_properties: true,
      bootstrap: { distinctID: params.distinctId },
      loaded: (ph) => {
        if (params.superProps) ph.register({ ...params.superProps, app_version: params.appVersion });
        else ph.register({ app_version: params.appVersion });
      },
    });
    client = posthog;
    // `captureEventName: false` suppresses PostHog's auto-fired `$opt_in`
    // event. We track the user-facing opt-in transition ourselves via the
    // `analytics_toggled` event in src/analytics/index.ts; without this flag,
    // `$opt_in` would fire on every page load (since `opt_out_capturing_by_default`
    // means we re-opt-in every boot), drowning the real signal in noise.
    if (params.optedInOnInit) posthog.opt_in_capturing({ captureEventName: false });
    const queued = buffer;
    buffer = [];
    for (const { name, props } of queued) posthog.capture(name, props);
  })();
  return initPromise;
}

export function track<N extends EventName>(name: N, props: EventProps<N>): void {
  const parsed = eventSchemas[name].safeParse(props);
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      throw new Error(`analytics: invalid props for ${String(name)}: ${parsed.error.message}`);
    }
    return;
  }
  if (!client) {
    buffer.push({ name, props: parsed.data as Record<string, unknown> });
    return;
  }
  client.capture(name as string, parsed.data as Record<string, unknown>);
}

export function setOptedIn(value: boolean): void {
  if (!client) return;
  // See note on the `opt_in_capturing` call in initPostHog for why we
  // suppress the auto-event.
  if (value) client.opt_in_capturing({ captureEventName: false });
  else client.opt_out_capturing();
}

export function getDistinctId(): string | undefined {
  return client?.get_distinct_id();
}

/**
 * Requests PostHog to delete all data for this person by sending a
 * `$delete_person` event to the ingest endpoint. PostHog processes
 * this as a deletion request (it queues a person-deletion job on the
 * server side). This is a best-effort call; the user should also be
 * opted out of capturing via setOptedIn(false).
 *
 * If the PostHog API ever changes this endpoint/shape, update both the
 * implementation and the comment here.
 */
export async function deleteMyData(): Promise<void> {
  if (!_apiKey || !_distinctId) return;
  await fetch(`${_apiHost}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: _apiKey,
      event: '$delete_person',
      distinct_id: _distinctId,
    }),
  });
}

/** @internal — only for tests. */
export function resetForTest(): void {
  client = null;
  buffer = [];
  initPromise = null;
  _apiKey = null;
  _distinctId = null;
}
